import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';
import { domainsHydratedAtom, domainsListParamsAtom } from '@/store';

export interface SearchFields {
  domain: string;
  organization: string;
}

export function useDomainsFilters() {
  const [params, setParams] = useAtom(domainsListParamsAtom);
  const [, setHydrated] = useAtom(domainsHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    search: parseAsString.withDefault(''),
    status: parseAsString.withDefault('all'),
    domain: parseAsString.withDefault(''),
    organization: parseAsString.withDefault(''),
    date: parseAsString.withDefault('')
  });

  // Filters state (client UI) mapped to API params (URL synced)
  const [statusFilter, setStatusFilter] = React.useState<string>(queryParams.status);
  const [searchFields, setSearchFields] = React.useState<SearchFields>({
    domain: queryParams.domain,
    organization: queryParams.organization
  });

  // Search state
  const [searchTerm, setSearchTerm] = React.useState(queryParams.search);

  // Date filter state
  const [dateFilter, setDateFilter] = React.useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  // Sync URL parameters with params atom on mount and when URL changes
  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};

    // Build filters from URL parameters
    if (queryParams.search) filters.search = queryParams.search;
    if (queryParams.status !== 'all') filters.status = queryParams.status;
    if (queryParams.domain) filters.domain = queryParams.domain;
    if (queryParams.organization) filters.organization = queryParams.organization;
    if (queryParams.date) filters.date = queryParams.date;

    // Only update if there's actually a change to avoid unnecessary API calls
    setParams((prev) => {
      const newParams = {
        ...prev,
        page: queryParams.page,
        filters: filters
      };

      // Check if anything actually changed
      if (prev.page === newParams.page &&
        JSON.stringify(prev.filters) === JSON.stringify(newParams.filters)) {
        return prev; // No change, return same object
      }

      return newParams;
    });

    // Mark hydrated after first effective sync
    setHydrated(true);
  }, [queryParams.page, queryParams.search, queryParams.status, queryParams.domain, queryParams.organization, queryParams.date, setParams, setHydrated]);

  // Sync URL parameters with local state on mount
  React.useEffect(() => {
    setSearchTerm(queryParams.search);
    setStatusFilter(queryParams.status);
    setSearchFields({
      domain: queryParams.domain,
      organization: queryParams.organization
    });

    // Parse date filter from URL
    if (queryParams.date) {
      const dates = queryParams.date.split(',');
      if (dates.length === 2) {
        setDateFilter({
          start: new Date(dates[0]),
          end: new Date(dates[1])
        });
      } else if (dates.length === 1 && dates[0]) {
        setDateFilter({
          start: new Date(dates[0]),
          end: null
        });
      }
    }
  }, []); // Only run on mount

  // Search handler
  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: searchTerm || undefined }
    }));
    setQueryParams({ page: 1, search: searchTerm });
  }, [searchTerm, setParams, setQueryParams]);

  // Clear search handler
  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: undefined }
    }));
    setQueryParams({ page: 1, search: '' });
  }, [setParams, setQueryParams]);

  const applyFilters = React.useCallback(async () => {
    const nextFilters: Record<string, string | undefined> = {
      ...params.filters,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      domain: searchFields.domain || undefined,
      organization: searchFields.organization || undefined
    };

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));

    await setQueryParams({
      page: 1,
      status: statusFilter,
      domain: searchFields.domain,
      organization: searchFields.organization
    });
  }, [params.filters, statusFilter, searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSearchFields({ domain: '', organization: '' });
    setDateFilter({ start: null, end: null });
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      search: '',
      status: 'all',
      domain: '',
      organization: '',
      date: ''
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(() => {
    return (
      Boolean(searchTerm) ||
      statusFilter !== 'all' ||
      Boolean(searchFields.domain || searchFields.organization) ||
      Boolean(dateFilter.start || dateFilter.end)
    );
  }, [searchTerm, statusFilter, searchFields, dateFilter]);

  const handleStatusChange = React.useCallback((val: string) => {
    setStatusFilter(val);
    setQueryParams({ page: 1, status: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, status: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  const handleDateChange = React.useCallback((value: { start: Date | null; end: Date | null }) => {
    setDateFilter(value);

    let dateFilterValue = '';
    if (value.start && value.end) {
      // Format dates as YYYY-MM-DD
      const startDate = value.start.toISOString().split('T')[0];
      const endDate = value.end.toISOString().split('T')[0];
      dateFilterValue = `${startDate},${endDate}`;
    } else if (value.start) {
      dateFilterValue = value.start.toISOString().split('T')[0];
    }

    setQueryParams({ page: 1, date: dateFilterValue });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: {
        ...prev.filters,
        date: dateFilterValue || undefined
      }
    }));
  }, [setParams, setQueryParams]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    statusFilter,
    searchFields,
    setSearchFields,
    dateFilter,
    hasAnyFilter,

    // Actions
    handleSearch,
    handleClearSearch,
    applyFilters,
    clearFilters,
    handleStatusChange,
    handleDateChange,
    handlePageChange
  };
}
